import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from 'react-query';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  DevicePhoneMobileIcon,
  BellIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  StarIcon,
  LinkIcon,
  ChevronRightIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const FCMTest = () => {
  const [activeTab, setActiveTab] = useState('individual'); // individual, bulk, templates
  const [selectedUser, setSelectedUser] = useState('');
  const [title, setTitle] = useState('테스트 푸시 알림');
  const [body, setBody] = useState('FCM 푸시 알림 테스트입니다.');
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchedCustomer, setSearchedCustomer] = useState(null);
  const [notificationType, setNotificationType] = useState('default'); // default, urgent, important
  const [url, setUrl] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [recentUsers, setRecentUsers] = useState([]);
  
  // 프로젝트 선택을 위한 상태
  const [projectFilter, setProjectFilter] = useState('all'); // all, received, review, progress, contract, completed
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectPage, setProjectPage] = useState(1);
  const projectsPerPage = 20;
  
  // 일괄 발송 관련 상태
  const [bulkTargetType, setBulkTargetType] = useState('unconfirmed'); // unconfirmed, all_admin, all_contractor, active_projects
  const [unconfirmedTime, setUnconfirmedTime] = useState('30'); // 30, 60, 120, 180, 240, 360, 1440, 2880, 4320, 7200 (분)
  const [selectedBulkUsers, setSelectedBulkUsers] = useState([]);
  const [bulkNotificationType, setBulkNotificationType] = useState('default'); // 일괄 발송 알림 타입
  
  // 미처리 프로젝트 템플릿
  const [unconfirmedTitle, setUnconfirmedTitle] = useState('미처리 프로젝트 알림');
  const [unconfirmedBodyTemplate, setUnconfirmedBodyTemplate] = useState('{name}님 {count}개 프로젝트가 {time} 이상 접수 상태입니다. 확인 부탁드립니다.');
  const [unconfirmedNotificationType, setUnconfirmedNotificationType] = useState('unprocessed'); // 미처리 전용 알림음
  const [unconfirmedTemplates, setUnconfirmedTemplates] = useState([
    '{name}님 {count}개 프로젝트가 {time} 이상 접수 상태입니다. 확인 부탁드립니다.',
    '{name}님, {time} 경과된 {count}개의 미처리 프로젝트가 있습니다.',
    '⚠️ {name}님! {count}개 프로젝트가 {time}째 대기중입니다. 신속한 처리 부탁드립니다.',
    '[긴급] {name}님, {time} 이상 미처리된 {count}개 프로젝트 확인이 필요합니다.'
  ]);
  const [editingTemplates, setEditingTemplates] = useState(false);
  
  // 데이터 삭제 관련 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteType, setDeleteType] = useState('fcm_history');
  const [deleteDays, setDeleteDays] = useState(30);
  
  // 템플릿 관련 상태
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    title: '',
    body: '',
    priority: 'default',
    url: ''
  });
  
  // 데이터 삭제 함수
  const handleDeleteOldData = async () => {
    try {
      console.log('Deleting FCM data:', { days: deleteDays, type: deleteType });
      const response = await api.delete('/analytics/cleanup', {
        data: {
          days: deleteDays,
          type: deleteType
        }
      });
      console.log('Delete response:', response.data);
      
      toast.success(`${deleteDays}일 이전 ${deleteType === 'fcm_history' ? 'FCM 발송 기록' : deleteType === 'fcm_devices' ? 'FCM 기기 데이터' : '모든 FCM 데이터'}이 삭제되었습니다.`);
      setShowDeleteModal(false);
      // 데이터 새로고침
      refetchHistory();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(`데이터 삭제에 실패했습니다: ${error.response?.data?.error || error.message}`);
    }
  };
  
  // 사용자 목록 조회 (관리자 + 소장 + VIP)
  const { data: users, error: usersError, isLoading: usersLoading, refetch } = useQuery(
    'fcm-users', // Date.now()를 제거하여 무한 리렌더링 방지
    async () => {
      try {
        const [admins, contractors, vipCustomersRes] = await Promise.all([
          api.get('/admin/users?user_type=admin'),
          api.get('/admin/contractors'),
          api.get('/admin/vip/customers')
        ]);
        
        // 응답 데이터 구조 확인 및 처리
        const adminsData = Array.isArray(admins.data) ? admins.data : [];
        
        const contractorsData = contractors.data?.contractors || contractors.data || [];
        const contractorsList = Array.isArray(contractorsData) ? contractorsData : [];
        
        const vipCustomersData = vipCustomersRes.data?.vipCustomers || vipCustomersRes.data || [];
        const vipCustomers = Array.isArray(vipCustomersData) ? vipCustomersData : [];
        
        console.log('API responses:', { 
          adminsResponse: admins.data,
          adminsData,
          contractorsResponse: contractors.data,
          contractorsList,
          vipCustomersResponse: vipCustomersRes.data,
          vipCustomers 
        });
        
        // 관리자, 소장, VIP만 표시
        return [
          ...adminsData.map(a => ({ ...a, display_type: '관리자' })),
          ...contractorsList.map(c => ({ ...c, display_type: '소장' })),
          ...vipCustomers.map(v => ({ 
            id: v.user_id, 
            name: v.User?.name || v.name || 'Unknown',
            phone: v.User?.phone || v.phone || '',
            user_type: 'vip_customer',
            display_type: 'VIP' 
          }))
        ];
      } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
      }
    },
    {
      staleTime: 0, // 데이터를 항상 stale로 간주
      cacheTime: 0, // 캐시하지 않음
      retry: 3,
      retryDelay: 1000
    }
  );
  
  // 등록된 디바이스 조회
  const { data: devices } = useQuery(
    ['fcm-devices', selectedUser],
    () => selectedUser ? api.get(`/fcm/devices?user_id=${selectedUser}`).then(res => res.data) : null,
    { enabled: !!selectedUser }
  );
  
  // 푸시 발송 내역 - 페이징 및 필터링 추가
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(20);
  const [historyUserFilter, setHistoryUserFilter] = useState(null); // 수신자 필터
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all'); // 상태 필터
  const [historyTypeFilter, setHistoryTypeFilter] = useState('all'); // 타입 필터
  const [historyDateFilter, setHistoryDateFilter] = useState('all'); // 날짜 필터
  const [historySearchTerm, setHistorySearchTerm] = useState(''); // 검색어
  const [historySortBy, setHistorySortBy] = useState('created_at'); // 정렬 기준
  const [historySortOrder, setHistorySortOrder] = useState('desc'); // 정렬 순서
  
  const { data: history, refetch: refetchHistory } = useQuery(
    ['fcm-history', historyPage, historyLimit, historyUserFilter, historyStatusFilter, 
     historyTypeFilter, historyDateFilter, historySearchTerm, historySortBy, historySortOrder],
    () => {
      const params = new URLSearchParams({
        page: historyPage,
        limit: historyLimit,
        sort_by: historySortBy,
        sort_order: historySortOrder
      });
      
      if (historyUserFilter) params.append('user_id', historyUserFilter.id);
      if (historyStatusFilter !== 'all') params.append('status', historyStatusFilter);
      if (historyTypeFilter !== 'all') params.append('type', historyTypeFilter);
      if (historyDateFilter !== 'all') params.append('date_filter', historyDateFilter);
      if (historySearchTerm) params.append('search', historySearchTerm);
      
      return api.get(`/fcm/history?${params.toString()}`).then(res => res.data);
    }
  );
  
  // 프로젝트 목록 조회 - 선택된 사용자 타입에 따라 필터링
  const selectedUserData = users?.find(u => u.id === parseInt(selectedUser));
  const isContractorSelected = selectedUserData?.user_type === 'contractor';
  const isCustomerSelected = selectedUserData?.user_type === 'customer' || selectedUserData?.user_type === 'vip_customer';
  
  const { data: projects } = useQuery(
    ['fcm-projects', projectFilter, projectSearch, projectPage, selectedUser, isContractorSelected, isCustomerSelected],
    () => {
      const params = new URLSearchParams();
      if (projectFilter !== 'all') params.append('status', projectFilter);
      if (projectSearch) params.append('search', projectSearch);
      
      // 선택된 사용자에 따라 필터링
      if (isContractorSelected && selectedUser) {
        params.append('contractor_id', selectedUser);
      } else if (isCustomerSelected && selectedUser) {
        params.append('customer_id', selectedUser);
      }
      
      params.append('page', projectPage);
      params.append('limit', projectsPerPage);
      
      return api.get(`/projects?${params.toString()}`).then(res => res.data);
    },
    { 
      keepPreviousData: true,
      enabled: showProjectModal // 모달이 열렸을 때만 조회
    }
  );
  
  // 미확인 프로젝트 조회
  const { data: unconfirmedProjects } = useQuery(
    ['unconfirmed-projects', unconfirmedTime],
    () => api.get(`/admin/unconfirmed-projects?minutes=${unconfirmedTime}`).then(res => res.data),
    { enabled: activeTab === 'bulk' && bulkTargetType === 'unconfirmed' }
  );
  
  // 활성 프로젝트 사용자 조회
  const { data: activeProjectUsers } = useQuery(
    'active-project-users',
    () => api.get('/admin/active-project-users').then(res => res.data),
    { enabled: activeTab === 'bulk' && bulkTargetType === 'active_projects' }
  );
  
  // 최근 선택 사용자 로드
  useEffect(() => {
    const saved = localStorage.getItem('fcm-recent-users');
    if (saved) {
      setRecentUsers(JSON.parse(saved));
    }
    
    // 템플릿 로드
    const savedTemplates = localStorage.getItem('fcm-templates');
    if (savedTemplates) {
      setTemplates(JSON.parse(savedTemplates));
    }
    
    // 미처리 프로젝트 템플릿 로드
    const savedUnconfirmedTitle = localStorage.getItem('fcm-unconfirmed-title');
    const savedUnconfirmedBody = localStorage.getItem('fcm-unconfirmed-body-template');
    const savedUnconfirmedType = localStorage.getItem('fcm-unconfirmed-notification-type');
    const savedUnconfirmedTemplates = localStorage.getItem('fcm-unconfirmed-templates');
    
    if (savedUnconfirmedTitle) {
      setUnconfirmedTitle(savedUnconfirmedTitle);
    }
    if (savedUnconfirmedBody) {
      setUnconfirmedBodyTemplate(savedUnconfirmedBody);
    }
    if (savedUnconfirmedType) {
      setUnconfirmedNotificationType(savedUnconfirmedType);
    }
    if (savedUnconfirmedTemplates) {
      setUnconfirmedTemplates(JSON.parse(savedUnconfirmedTemplates));
    }
  }, []);
  
  // 프로젝트 선택 시 URL 자동 생성
  useEffect(() => {
    if (selectedProject && users && selectedUser) {
      const user = users.find(u => u.id === parseInt(selectedUser));
      const project = projects?.projects?.find(p => p.id === parseInt(selectedProject));
      
      if (user && project) {
        if (user.user_type === 'customer' || user.user_type === 'vip_customer') {
          // 고객용 URL
          setUrl(`/h/${project.id}/${project.customer_password}`);
        } else {
          // 관리자/소장용 URL
          setUrl(`/p/${project.id}`);
        }
      }
    }
  }, [selectedProject, selectedUser, users, projects]);
  
  // 푸시 테스트 발송
  const sendTestPush = useMutation(
    (data) => api.post('/fcm/test', data),
    {
      onSuccess: () => {
        toast.success('푸시 알림을 발송했습니다.');
        setTitle('테스트 푸시 알림');
        setBody('FCM 푸시 알림 테스트입니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '푸시 발송에 실패했습니다.');
      }
    }
  );
  
  // 일괄 푸시 발송
  const sendBulkPush = useMutation(
    (data) => api.post('/fcm/bulk', data),
    {
      onSuccess: (response) => {
        toast.success(`${response.data.successCount}명에게 푸시 알림을 발송했습니다.`);
        setSelectedBulkUsers([]);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '일괄 발송에 실패했습니다.');
      }
    }
  );
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedUser) {
      toast.error('사용자를 선택해주세요.');
      return;
    }
    
    // URL 처리 - 상대 경로는 그대로 유지
    let processedUrl = url.trim();
    if (processedUrl && !processedUrl.startsWith('/') && !processedUrl.startsWith('http')) {
      // 슬래시로 시작하지 않는 경로는 슬래시 추가
      processedUrl = '/' + processedUrl;
    }
    
    // 알림음 매핑 (실제 시스템과 동일하게)
    const soundMap = {
      'default': 'a_default',
      'urgent': 'a_urgent',   // 긴급 → a_urgent.mp3
      'important': 'a_important',  // 중요 → a_important.mp3
      'unprocessed': 'a_unprocessed'  // 미처리 → a_unprocessed.mp3
    };
    
    console.log('FCM Test - Sending with sound:', soundMap[notificationType], 'for type:', notificationType);
    
    const testData = {
      user_id: parseInt(selectedUser),
      title,
      body,
      url: processedUrl, // 상대 경로 그대로 전송
      priority: notificationType,
      sound: soundMap[notificationType] || 'a_default'
    };
    
    console.log('FCM Test - Sending data:', testData);
    sendTestPush.mutate(testData);
    
    // 최근 사용자 저장
    const userInfo = users.find(u => u.id === parseInt(selectedUser));
    if (userInfo) {
      const newRecent = [
        { id: userInfo.id, name: userInfo.name, type: userInfo.display_type },
        ...recentUsers.filter(u => u.id !== userInfo.id)
      ].slice(0, 5);
      setRecentUsers(newRecent);
      localStorage.setItem('fcm-recent-users', JSON.stringify(newRecent));
    }
  };
  
  const handleBulkSubmit = () => {
    if (selectedBulkUsers.length === 0) {
      toast.error('발송 대상을 선택해주세요.');
      return;
    }
    
    // 발송 대상 타입에 따른 메시지 처리
    let bulkData = [];
    
    // 알림음 매핑
    const soundMap = {
      'default': 'a_default',
      'urgent': 'a_urgent',
      'important': 'a_important',
      'unprocessed': 'a_unprocessed'
    };
    
    if (bulkTargetType === 'unconfirmed') {
      // 미처리 프로젝트 리마인더
      const timeMap = {
        '30': '30분',
        '60': '1시간',
        '120': '2시간',
        '180': '3시간',
        '240': '4시간',
        '360': '6시간',
        '1440': '1일',
        '2880': '2일',
        '4320': '3일',
        '7200': '5일'
      };
      
      bulkData = selectedBulkUsers.map(user => {
        // 템플릿 변수 치환
        const body = unconfirmedBodyTemplate
          .replace('{name}', user.name)
          .replace('{count}', user.unconfirmedCount)
          .replace('{time}', timeMap[unconfirmedTime]);
        
        const notification = {
          user_id: user.id,
          title: unconfirmedTitle,
          body: body,
          url: '/projects?filter=received',
          priority: unconfirmedNotificationType,
          sound: soundMap[unconfirmedNotificationType]
        };
        
        console.log('Unconfirmed notification data:', notification);
        console.log('Sound mapping:', unconfirmedNotificationType, '->', soundMap[unconfirmedNotificationType]);
        
        return notification;
      });
    } else if (bulkTargetType === 'all_admin') {
      // 전체 관리자
      bulkData = selectedBulkUsers.map(user => ({
        user_id: user.id,
        title: '관리자 알림',
        body: `${user.name}님, 시스템 알림을 확인해주세요.`,
        url: '/projects',
        priority: bulkNotificationType,
        sound: soundMap[bulkNotificationType]
      }));
    } else if (bulkTargetType === 'all_contractor') {
      // 전체 소장
      bulkData = selectedBulkUsers.map(user => ({
        user_id: user.id,
        title: '소장 알림',
        body: `${user.name}님, 프로젝트 현황을 확인해주세요.`,
        url: '/projects',
        priority: bulkNotificationType,
        sound: soundMap[bulkNotificationType]
      }));
    } else if (bulkTargetType === 'active_projects') {
      // 활성 프로젝트 관련자
      bulkData = selectedBulkUsers.map(user => ({
        user_id: user.id,
        title: '프로젝트 알림',
        body: `${user.name}님, 진행 중인 프로젝트를 확인해주세요.`,
        url: '/projects?filter=progress',
        priority: bulkNotificationType,
        sound: soundMap[bulkNotificationType]
      }));
    }
    
    sendBulkPush.mutate({ notifications: bulkData });
  };
  
  if (usersLoading) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">FCM 푸시 알림 테스트</h1>
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">사용자 목록을 불러오는 중...</div>
        </div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">FCM 푸시 알림 테스트</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="text-red-800">
            <p className="font-semibold mb-2">사용자 목록을 불러오는 중 오류가 발생했습니다</p>
            <p className="text-sm">{usersError.message || '알 수 없는 오류'}</p>
            {usersError.response && (
              <p className="text-xs mt-2">상태 코드: {usersError.response.status}</p>
            )}
          </div>
          <button 
            onClick={() => refetch()} 
            className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md text-sm hover:bg-red-700"
          >
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">FCM 푸시 알림 테스트</h1>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <TrashIcon className="h-5 w-5" />
          데이터 정리
        </button>
      </div>
      
      {/* 탭 네비게이션 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('individual')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'individual'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            개별 발송
          </button>
          <button
            onClick={() => setActiveTab('bulk')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'bulk'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            일괄 발송
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            템플릿 관리
          </button>
        </nav>
      </div>
      
      {/* 사용자 목록 디버깅 정보 */}
      {users && users.length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="text-yellow-800">
            표시할 사용자가 없습니다. 관리자, 소장, VIP 고객이 등록되어 있는지 확인해주세요.
          </div>
        </div>
      )}
      
      {/* 개별 발송 탭 */}
      {activeTab === 'individual' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <PaperAirplaneIcon className="h-5 w-5 mr-2" />
          테스트 푸시 발송
        </h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. 수신자 선택 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. 수신자 선택
            </label>
            
            {/* 최근 선택 사용자 */}
            {recentUsers.length > 0 && (
              <div className="mb-2">
                <span className="text-xs text-gray-500">최근 선택:</span>
                <div className="flex gap-2 mt-1">
                  {recentUsers.map((user) => (
                    <button
                      key={user.id}
                      type="button"
                      onClick={() => setSelectedUser(user.id.toString())}
                      className={`px-3 py-1 text-xs rounded-full ${
                        selectedUser === user.id.toString()
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {user.name} ({user.type})
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            <select
              value={selectedUser}
              onChange={(e) => setSelectedUser(e.target.value)}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">사용자를 선택하세요</option>
              <optgroup label="관리자">
                {users?.filter(u => u.display_type === '관리자').map((user) => (
                  <option key={`admin-${user.id}`} value={user.id}>
                    {user.name} - ID: {user.login_id || user.email} ({user.phone})
                  </option>
                ))}
              </optgroup>
              <optgroup label="소장">
                {users?.filter(u => u.display_type === '소장').map((user) => (
                  <option key={`contractor-${user.id}`} value={user.id}>
                    {user.name} - ID: {user.login_id || user.email} ({user.phone}) - {user.company_name || '소장'}
                  </option>
                ))}
              </optgroup>
              <optgroup label="VIP 고객">
                {users?.filter(u => u.display_type === 'VIP').map((user) => (
                  <option key={`vip-${user.id}`} value={user.id}>
                    {user.name} ({user.phone})
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
          
          {/* 일반 고객 검색 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              일반 고객 검색 (전화번호 또는 이름)
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                placeholder="전화번호 또는 이름 입력"
                className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!customerSearch.trim()) {
                    toast.error('검색어를 입력해주세요.');
                    return;
                  }
                  try {
                    const response = await api.get(`/admin/customers/search?q=${encodeURIComponent(customerSearch)}`);
                    console.log('Search response:', response.data);
                    if (response.data && response.data.length > 0) {
                      const customer = response.data[0];
                      setSearchedCustomer(customer);
                      setSelectedUser(customer.id);
                      toast.success(`${customer.name}님을 찾았습니다.`);
                    } else {
                      toast.error('고객을 찾을 수 없습니다.');
                    }
                  } catch (error) {
                    console.error('Search error:', error);
                    toast.error(error.response?.data?.error || '검색 중 오류가 발생했습니다.');
                  }
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                검색
              </button>
            </div>
            {searchedCustomer && (
              <div className="mt-2 p-3 bg-green-50 rounded-md">
                <p className="text-sm text-green-700">
                  선택된 고객: {searchedCustomer.name} ({searchedCustomer.phone})
                </p>
              </div>
            )}
          </div>
          
          {selectedUser && devices && devices.length > 0 && (
            <div className="bg-blue-50 p-3 rounded-md">
              <p className="text-sm text-blue-700">
                <DevicePhoneMobileIcon className="inline h-4 w-4 mr-1" />
                등록된 디바이스: {devices.length}개
              </p>
            </div>
          )}
          
          {/* 2. 알림 유형 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              2. 알림 유형
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setNotificationType('default')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                  notificationType === 'default'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <BellIcon className="h-4 w-4 mr-1" />
                일반
              </button>
              <button
                type="button"
                onClick={() => setNotificationType('urgent')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                  notificationType === 'urgent'
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                🚨 긴급
              </button>
              <button
                type="button"
                onClick={() => setNotificationType('important')}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                  notificationType === 'important'
                    ? 'bg-amber-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <StarIcon className="h-4 w-4 mr-1" />
                ⭐ 중요
              </button>
            </div>
          </div>
          
          {/* 3. 이동 경로 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              3. 이동 경로 (선택사항)
            </label>
            <div className="space-y-2">
              {/* 프로젝트 선택 버튼 */}
              <div>
                <button
                  type="button"
                  onClick={() => {
                    setShowProjectModal(true);
                    setProjectFilter('all');
                    setProjectSearch('');
                    setProjectPage(1);
                  }}
                  className="w-full px-4 py-2 text-left border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-between"
                >
                  <span className={selectedProject ? 'text-gray-900' : 'text-gray-500'}>
                    {selectedProject ? 
                      projects?.projects?.find(p => p.id === parseInt(selectedProject))?.title || '프로젝트 선택' 
                      : '프로젝트를 선택하세요 (자동 URL 생성)'
                    }
                  </span>
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              
              {/* URL 직접 입력 */}
              <div>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <LinkIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="/p/95 또는 https://2.youngsam.net/p/95"
                    className="block w-full pl-10 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  예: /p/95, /h/95/1234, https://2.youngsam.net/p/95
                </p>
              </div>
            </div>
          </div>
          
          {/* 4. 메시지 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              4. 메시지 내용
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="제목"
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="내용"
                rows={3}
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                required
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={sendTestPush.isLoading}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
          >
            {sendTestPush.isLoading ? '발송 중...' : '푸시 발송'}
          </button>
        </form>
        </div>
      )}
      
      {/* 일괄 발송 탭 */}
      {activeTab === 'bulk' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PaperAirplaneIcon className="h-5 w-5 mr-2" />
            일괄 푸시 발송
          </h2>
          
          <div className="space-y-4">
            {/* 발송 대상 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                발송 대상
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setBulkTargetType('unconfirmed')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    bulkTargetType === 'unconfirmed'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  미처리 프로젝트(소장)
                </button>
                <button
                  type="button"
                  onClick={() => setBulkTargetType('all_admin')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    bulkTargetType === 'all_admin'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체 관리자
                </button>
                <button
                  type="button"
                  onClick={() => setBulkTargetType('all_contractor')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    bulkTargetType === 'all_contractor'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  전체 소장
                </button>
                <button
                  type="button"
                  onClick={() => setBulkTargetType('active_projects')}
                  className={`px-4 py-2 rounded-md text-sm font-medium ${
                    bulkTargetType === 'active_projects'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  진행중인 프로젝트 관련자
                </button>
              </div>
            </div>
            
            {/* 미처리 프로젝트 설정 */}
            {bulkTargetType === 'unconfirmed' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    미처리 시간
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      ['30', '30분'], ['60', '1시간'], ['120', '2시간'], ['180', '3시간'], ['240', '4시간'],
                      ['360', '6시간'], ['1440', '1일'], ['2880', '2일'], ['4320', '3일'], ['7200', '5일']
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setUnconfirmedTime(value)}
                        className={`px-3 py-2 rounded-md text-sm font-medium ${
                          unconfirmedTime === value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* 알림 템플릿 설정 */}
                <div className="space-y-3">
                  {/* 알림음 선택 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      알림음 선택
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setUnconfirmedNotificationType('default');
                          localStorage.setItem('fcm-unconfirmed-notification-type', 'default');
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                          unconfirmedNotificationType === 'default'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        🔔 일반
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setUnconfirmedNotificationType('unprocessed');
                          localStorage.setItem('fcm-unconfirmed-notification-type', 'unprocessed');
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                          unconfirmedNotificationType === 'unprocessed'
                            ? 'bg-orange-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ⏰ 미처리
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setUnconfirmedNotificationType('urgent');
                          localStorage.setItem('fcm-unconfirmed-notification-type', 'urgent');
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                          unconfirmedNotificationType === 'urgent'
                            ? 'bg-red-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        🚨 긴급
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setUnconfirmedNotificationType('important');
                          localStorage.setItem('fcm-unconfirmed-notification-type', 'important');
                        }}
                        className={`px-3 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 ${
                          unconfirmedNotificationType === 'important'
                            ? 'bg-yellow-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        ⭐ 중요
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      알림 제목
                    </label>
                    <input
                      type="text"
                      value={unconfirmedTitle}
                      onChange={(e) => {
                        setUnconfirmedTitle(e.target.value);
                        localStorage.setItem('fcm-unconfirmed-title', e.target.value);
                      }}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        알림 내용 템플릿
                      </label>
                      <button
                        type="button"
                        onClick={() => setEditingTemplates(!editingTemplates)}
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        {editingTemplates ? '완료' : '편집'}
                      </button>
                    </div>
                    
                    {!editingTemplates ? (
                      <>
                        <select
                          value={unconfirmedBodyTemplate}
                          onChange={(e) => {
                            setUnconfirmedBodyTemplate(e.target.value);
                            localStorage.setItem('fcm-unconfirmed-body-template', e.target.value);
                          }}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 mb-2"
                        >
                          {unconfirmedTemplates.map((template, index) => (
                            <option key={index} value={template}>
                              {template.length > 50 ? template.substring(0, 50) + '...' : template}
                            </option>
                          ))}
                        </select>
                        <textarea
                          value={unconfirmedBodyTemplate}
                          onChange={(e) => {
                            setUnconfirmedBodyTemplate(e.target.value);
                            localStorage.setItem('fcm-unconfirmed-body-template', e.target.value);
                          }}
                          rows={2}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </>
                    ) : (
                      <div className="space-y-2">
                        {unconfirmedTemplates.map((template, index) => (
                          <div key={index} className="flex gap-2">
                            <textarea
                              value={template}
                              onChange={(e) => {
                                const newTemplates = [...unconfirmedTemplates];
                                newTemplates[index] = e.target.value;
                                setUnconfirmedTemplates(newTemplates);
                                localStorage.setItem('fcm-unconfirmed-templates', JSON.stringify(newTemplates));
                              }}
                              rows={2}
                              className="flex-1 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const newTemplates = unconfirmedTemplates.filter((_, i) => i !== index);
                                setUnconfirmedTemplates(newTemplates);
                                localStorage.setItem('fcm-unconfirmed-templates', JSON.stringify(newTemplates));
                              }}
                              className="px-2 py-1 text-red-600 hover:text-red-700"
                            >
                              삭제
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => {
                            const newTemplates = [...unconfirmedTemplates, '새 템플릿을 입력하세요'];
                            setUnconfirmedTemplates(newTemplates);
                            localStorage.setItem('fcm-unconfirmed-templates', JSON.stringify(newTemplates));
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                        >
                          + 템플릿 추가
                        </button>
                      </div>
                    )}
                    
                    <p className="mt-1 text-xs text-gray-500">
                      사용 가능한 변수: {'{name}'} (이름), {'{count}'} (프로젝트 수), {'{time}'} (경과 시간)
                    </p>
                  </div>
                </div>
              </>
            )}
            
            {/* 발송 대상 목록 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                발송 대상 ({selectedBulkUsers.length}명 선택됨)
              </label>
              <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                {bulkTargetType === 'unconfirmed' && unconfirmedProjects ? (
                  <div className="divide-y divide-gray-200">
                    {unconfirmedProjects.map((item) => (
                      <label
                        key={`${item.user_type}-${item.user_id}`}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBulkUsers.some(u => u.id === item.user_id && u.type === item.user_type)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBulkUsers([...selectedBulkUsers, {
                                id: item.user_id,
                                name: item.user_name,
                                type: item.user_type,
                                unconfirmedCount: item.unconfirmed_count
                              }]);
                            } else {
                              setSelectedBulkUsers(selectedBulkUsers.filter(
                                u => !(u.id === item.user_id && u.type === item.user_type)
                              ));
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {item.user_name} ({item.user_type === 'admin' ? '관리자' : '소장'})
                          </div>
                          <div className="text-sm text-gray-500">
                            미처리 프로젝트: {item.unconfirmed_count}개
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : bulkTargetType === 'all_admin' ? (
                  <div className="divide-y divide-gray-200">
                    {users?.filter(u => u.display_type === '관리자').map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBulkUsers.some(u => u.id === user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBulkUsers([...selectedBulkUsers, {
                                id: user.id,
                                name: user.name,
                                type: 'admin'
                              }]);
                            } else {
                              setSelectedBulkUsers(selectedBulkUsers.filter(u => u.id !== user.id));
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.login_id || user.email}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : bulkTargetType === 'all_contractor' ? (
                  <div className="divide-y divide-gray-200">
                    {users?.filter(u => u.display_type === '소장').map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBulkUsers.some(u => u.id === user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBulkUsers([...selectedBulkUsers, {
                                id: user.id,
                                name: user.name,
                                type: 'contractor'
                              }]);
                            } else {
                              setSelectedBulkUsers(selectedBulkUsers.filter(u => u.id !== user.id));
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{user.name}</div>
                          <div className="text-sm text-gray-500">{user.company_name || '소장'}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : bulkTargetType === 'active_projects' && activeProjectUsers ? (
                  <div className="divide-y divide-gray-200">
                    {activeProjectUsers.map((user) => (
                      <label
                        key={`${user.user_type}-${user.user_id}`}
                        className="flex items-center p-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedBulkUsers.some(u => u.id === user.user_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedBulkUsers([...selectedBulkUsers, {
                                id: user.user_id,
                                name: user.user_name,
                                type: user.user_type,
                                projectCount: user.active_project_count
                              }]);
                            } else {
                              setSelectedBulkUsers(selectedBulkUsers.filter(u => u.id !== user.user_id));
                            }
                          }}
                          className="h-4 w-4 text-indigo-600 rounded"
                        />
                        <div className="ml-3 flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {user.user_name} 
                            <span className="text-gray-500 ml-1">
                              ({user.user_type === 'admin' ? '관리자' : 
                                user.user_type === 'contractor' ? '소장' : 
                                user.user_type === 'vip_customer' ? 'VIP' : '고객'})
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            진행 중인 프로젝트: {user.active_project_count}개
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    발송 대상이 없습니다.
                  </div>
                )}
              </div>
            </div>
            
            {/* 전체 선택/해제 버튼 */}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (bulkTargetType === 'unconfirmed' && unconfirmedProjects) {
                    setSelectedBulkUsers(unconfirmedProjects.map(item => ({
                      id: item.user_id,
                      name: item.user_name,
                      type: item.user_type,
                      unconfirmedCount: item.unconfirmed_count
                    })));
                  } else if (bulkTargetType === 'all_admin') {
                    setSelectedBulkUsers(users?.filter(u => u.display_type === '관리자').map(u => ({
                      id: u.id,
                      name: u.name,
                      type: 'admin'
                    })) || []);
                  } else if (bulkTargetType === 'all_contractor') {
                    setSelectedBulkUsers(users?.filter(u => u.display_type === '소장').map(u => ({
                      id: u.id,
                      name: u.name,
                      type: 'contractor'
                    })) || []);
                  } else if (bulkTargetType === 'active_projects' && activeProjectUsers) {
                    setSelectedBulkUsers(activeProjectUsers.map(user => ({
                      id: user.user_id,
                      name: user.user_name,
                      type: user.user_type,
                      projectCount: user.active_project_count
                    })));
                  }
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                전체 선택
              </button>
              <button
                type="button"
                onClick={() => setSelectedBulkUsers([])}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                전체 해제
              </button>
            </div>
            
            {/* 알림 유형 선택 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                알림 유형
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setBulkNotificationType('default')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                    bulkNotificationType === 'default'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BellIcon className="h-4 w-4 mr-1" />
                  일반
                </button>
                <button
                  type="button"
                  onClick={() => setBulkNotificationType('urgent')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                    bulkNotificationType === 'urgent'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                  🚨 긴급
                </button>
                <button
                  type="button"
                  onClick={() => setBulkNotificationType('important')}
                  className={`flex items-center px-4 py-2 rounded-md text-sm font-medium ${
                    bulkNotificationType === 'important'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <StarIcon className="h-4 w-4 mr-1" />
                  ⭐ 중요
                </button>
              </div>
            </div>
            
            {/* 발송 버튼 */}
            <button
              type="button"
              onClick={handleBulkSubmit}
              disabled={selectedBulkUsers.length === 0 || sendBulkPush.isLoading}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
            >
              {sendBulkPush.isLoading ? '발송 중...' : `${selectedBulkUsers.length}명에게 일괄 발송`}
            </button>
          </div>
        </div>
      )}
      
      {/* 템플릿 관리 탭 */}
      {activeTab === 'templates' && (
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">푸시 템플릿 관리</h2>
          
          {/* 템플릿 목록 */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-md font-medium text-gray-700">저장된 템플릿</h3>
              <button
                onClick={() => setIsCreatingTemplate(true)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                새 템플릿 추가
              </button>
            </div>
            
            {templates.length > 0 ? (
              <div className="space-y-2">
                {templates.map((template, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => {
                      setTitle(template.title);
                      setBody(template.body);
                      setNotificationType(template.priority);
                      setUrl(template.url);
                      setActiveTab('individual');
                      toast.success('템플릿이 적용되었습니다.');
                    }}
                  >
                    <div className="font-medium text-gray-900">{template.name}</div>
                    <div className="text-sm text-gray-600 mt-1">{template.title}</div>
                    <div className="text-xs text-gray-500 mt-1">{template.body}</div>
                    <div className="flex gap-2 mt-2">
                      <span className={`text-xs px-2 py-1 rounded ${
                        template.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        template.priority === 'important' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {template.priority === 'urgent' ? '긴급' :
                         template.priority === 'important' ? '중요' : '일반'}
                      </span>
                      {template.url && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">
                          URL: {template.url}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const newTemplates = templates.filter((_, i) => i !== index);
                        setTemplates(newTemplates);
                        localStorage.setItem('fcm-templates', JSON.stringify(newTemplates));
                        toast.success('템플릿이 삭제되었습니다.');
                      }}
                      className="mt-2 text-xs text-red-600 hover:text-red-800"
                    >
                      삭제
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8">
                저장된 템플릿이 없습니다.
              </p>
            )}
          </div>
          
          {/* 템플릿 생성 폼 */}
          {isCreatingTemplate && (
            <div className="border-t pt-4">
              <h3 className="text-md font-medium text-gray-700 mb-4">새 템플릿 만들기</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    템플릿 이름
                  </label>
                  <input
                    type="text"
                    value={templateForm.name}
                    onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="예: 긴급 공사 알림"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    제목
                  </label>
                  <input
                    type="text"
                    value={templateForm.title}
                    onChange={(e) => setTemplateForm({...templateForm, title: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    내용
                  </label>
                  <textarea
                    value={templateForm.body}
                    onChange={(e) => setTemplateForm({...templateForm, body: e.target.value})}
                    rows={3}
                    className="block w-full border-gray-300 rounded-md shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    중요도
                  </label>
                  <div className="flex gap-2">
                    {[['default', '일반'], ['urgent', '긴급'], ['important', '중요']].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setTemplateForm({...templateForm, priority: value})}
                        className={`px-4 py-2 rounded-md text-sm font-medium ${
                          templateForm.priority === value
                            ? 'bg-indigo-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    URL (선택사항)
                  </label>
                  <input
                    type="text"
                    value={templateForm.url}
                    onChange={(e) => setTemplateForm({...templateForm, url: e.target.value})}
                    className="block w-full border-gray-300 rounded-md shadow-sm"
                    placeholder="/p/95 또는 /projects"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      if (!templateForm.name || !templateForm.title || !templateForm.body) {
                        toast.error('템플릿 이름, 제목, 내용을 모두 입력해주세요.');
                        return;
                      }
                      
                      const newTemplates = [...templates, templateForm];
                      setTemplates(newTemplates);
                      localStorage.setItem('fcm-templates', JSON.stringify(newTemplates));
                      
                      setTemplateForm({
                        name: '',
                        title: '',
                        body: '',
                        priority: 'default',
                        url: ''
                      });
                      setIsCreatingTemplate(false);
                      toast.success('템플릿이 저장되었습니다.');
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    저장
                  </button>
                  <button
                    onClick={() => {
                      setIsCreatingTemplate(false);
                      setTemplateForm({
                        name: '',
                        title: '',
                        body: '',
                        priority: 'default',
                        url: ''
                      });
                    }}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 발송 내역 */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-medium text-gray-900 flex items-center">
                <BellIcon className="h-5 w-5 mr-2" />
                최근 푸시 발송 내역
              </h2>
              {historyUserFilter && (
                <div className="flex items-center gap-2 px-3 py-1 bg-indigo-100 rounded-full">
                  <span className="text-sm text-indigo-700">
                    {historyUserFilter.name} 님의 내역만 표시
                  </span>
                  <button
                    onClick={() => {
                      setHistoryUserFilter(null);
                      setHistoryPage(1);
                    }}
                    className="ml-1 text-indigo-600 hover:text-indigo-800"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => refetchHistory()}
                className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                새로고침
              </button>
              {history?.totalCount && (
                <span className="text-sm text-gray-500">
                  전체 {history.totalCount}건
                </span>
              )}
            </div>
          </div>
          
          {/* 필터 및 검색 영역 */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              {/* 검색창 */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="제목, 내용으로 검색..."
                    value={historySearchTerm}
                    onChange={(e) => {
                      setHistorySearchTerm(e.target.value);
                      setHistoryPage(1);
                    }}
                    className="w-full px-3 py-1.5 text-sm border-gray-300 rounded-md pl-8"
                  />
                  <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              
              {/* 상태 필터 */}
              <select
                value={historyStatusFilter}
                onChange={(e) => {
                  setHistoryStatusFilter(e.target.value);
                  setHistoryPage(1);
                }}
                className="text-sm border-gray-300 rounded-md"
              >
                <option value="all">모든 상태</option>
                <option value="sent">발송됨</option>
                <option value="delivered">수신됨</option>
                <option value="failed">실패</option>
                <option value="pending">대기</option>
              </select>
              
              {/* 타입 필터 */}
              <select
                value={historyTypeFilter}
                onChange={(e) => {
                  setHistoryTypeFilter(e.target.value);
                  setHistoryPage(1);
                }}
                className="text-sm border-gray-300 rounded-md"
              >
                <option value="all">모든 타입</option>
                <option value="test">테스트</option>
                <option value="project_status">상태변경</option>
                <option value="message">메시지</option>
                <option value="unprocessed_reminder">미처리</option>
                <option value="bulk">일괄발송</option>
              </select>
              
              {/* 날짜 필터 */}
              <select
                value={historyDateFilter}
                onChange={(e) => {
                  setHistoryDateFilter(e.target.value);
                  setHistoryPage(1);
                }}
                className="text-sm border-gray-300 rounded-md"
              >
                <option value="all">전체 기간</option>
                <option value="today">오늘</option>
                <option value="yesterday">어제</option>
                <option value="week">최근 7일</option>
                <option value="month">최근 30일</option>
              </select>
              
              {/* 표시 개수 */}
              <select
                value={historyLimit}
                onChange={(e) => {
                  setHistoryLimit(parseInt(e.target.value));
                  setHistoryPage(1);
                }}
                className="text-sm border-gray-300 rounded-md"
              >
                <option value="10">10개씩</option>
                <option value="20">20개씩</option>
                <option value="50">50개씩</option>
                <option value="100">100개씩</option>
              </select>
            </div>
            
            {/* 적용된 필터 표시 */}
            {(historyStatusFilter !== 'all' || historyTypeFilter !== 'all' || 
              historyDateFilter !== 'all' || historySearchTerm) && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">필터:</span>
                {historyStatusFilter !== 'all' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                    {historyStatusFilter === 'sent' ? '발송됨' :
                     historyStatusFilter === 'delivered' ? '수신됨' :
                     historyStatusFilter === 'failed' ? '실패' : '대기'}
                  </span>
                )}
                {historyTypeFilter !== 'all' && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">
                    {historyTypeFilter}
                  </span>
                )}
                {historyDateFilter !== 'all' && (
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full">
                    {historyDateFilter === 'today' ? '오늘' :
                     historyDateFilter === 'yesterday' ? '어제' :
                     historyDateFilter === 'week' ? '최근 7일' : '최근 30일'}
                  </span>
                )}
                {historySearchTerm && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                    "{historySearchTerm}"
                  </span>
                )}
                <button
                  onClick={() => {
                    setHistoryStatusFilter('all');
                    setHistoryTypeFilter('all');
                    setHistoryDateFilter('all');
                    setHistorySearchTerm('');
                    setHistoryPage(1);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
        
        {history?.history && history.history.length > 0 ? (
          <div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => {
                        if (historySortBy === 'created_at') {
                          setHistorySortOrder(historySortOrder === 'desc' ? 'asc' : 'desc');
                        } else {
                          setHistorySortBy('created_at');
                          setHistorySortOrder('desc');
                        }
                      }}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      발송일시
                      {historySortBy === 'created_at' && (
                        <span className="text-indigo-600">
                          {historySortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => {
                        if (historySortBy === 'user_name') {
                          setHistorySortOrder(historySortOrder === 'desc' ? 'asc' : 'desc');
                        } else {
                          setHistorySortBy('user_name');
                          setHistorySortOrder('asc');
                        }
                      }}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      수신자
                      {historySortBy === 'user_name' && (
                        <span className="text-indigo-600">
                          {historySortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    제목
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => {
                        if (historySortBy === 'status') {
                          setHistorySortOrder(historySortOrder === 'desc' ? 'asc' : 'desc');
                        } else {
                          setHistorySortBy('status');
                          setHistorySortOrder('asc');
                        }
                      }}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      상태
                      {historySortBy === 'status' && (
                        <span className="text-indigo-600">
                          {historySortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <button
                      onClick={() => {
                        if (historySortBy === 'notification_type') {
                          setHistorySortOrder(historySortOrder === 'desc' ? 'asc' : 'desc');
                        } else {
                          setHistorySortBy('notification_type');
                          setHistorySortOrder('asc');
                        }
                      }}
                      className="flex items-center gap-1 hover:text-gray-700"
                    >
                      타입
                      {historySortBy === 'notification_type' && (
                        <span className="text-indigo-600">
                          {historySortOrder === 'desc' ? '↓' : '↑'}
                        </span>
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {history.history.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {item.user_name ? (
                        <button
                          onClick={() => {
                            setHistoryUserFilter({
                              id: item.user_id,
                              name: item.user_name
                            });
                            setHistoryPage(1);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 hover:underline font-medium"
                        >
                          {item.user_name}
                        </button>
                      ) : (
                        <span className="text-gray-900">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {item.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.status === 'sent' ? 'bg-green-100 text-green-800' :
                        item.status === 'delivered' ? 'bg-blue-100 text-blue-800' :
                        item.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status === 'sent' ? '발송됨' :
                         item.status === 'delivered' ? '수신됨' :
                         item.status === 'failed' ? '실패' : '대기'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.notification_type}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* 페이징 버튼 */}
          {history?.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {history.totalCount}개 중 {((historyPage - 1) * historyLimit) + 1}-
                {Math.min(historyPage * historyLimit, history.totalCount)}번째 표시
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                  disabled={historyPage === 1}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  이전
                </button>
                
                {/* 페이지 번호 표시 */}
                <div className="flex items-center gap-1">
                  {historyPage > 3 && (
                    <>
                      <button
                        onClick={() => setHistoryPage(1)}
                        className="px-3 py-1 text-sm border rounded-md hover:bg-gray-50"
                      >
                        1
                      </button>
                      {historyPage > 4 && <span className="px-2">...</span>}
                    </>
                  )}
                  
                  {[...Array(Math.min(5, history.totalPages))].map((_, idx) => {
                    const pageNum = historyPage <= 3 
                      ? idx + 1
                      : historyPage >= history.totalPages - 2
                        ? history.totalPages - 4 + idx
                        : historyPage - 2 + idx;
                    
                    if (pageNum < 1 || pageNum > history.totalPages) return null;
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setHistoryPage(pageNum)}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          pageNum === historyPage 
                            ? 'bg-indigo-600 text-white' 
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  {historyPage < history.totalPages - 2 && (
                    <>
                      {historyPage < history.totalPages - 3 && <span className="px-2">...</span>}
                      <button
                        onClick={() => setHistoryPage(history.totalPages)}
                        className="px-3 py-1 text-sm border rounded-md hover:bg-gray-50"
                      >
                        {history.totalPages}
                      </button>
                    </>
                  )}
                </div>
                
                <button
                  onClick={() => setHistoryPage(p => Math.min(history.totalPages, p + 1))}
                  disabled={historyPage === history.totalPages}
                  className="px-3 py-1 text-sm border rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  다음
                </button>
              </div>
            </div>
          )}
        </div>
        ) : (
          <p className="text-sm text-gray-500">아직 발송 내역이 없습니다.</p>
        )}
      </div>
      
      {/* 프로젝트 선택 모달 */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* 모달 헤더 */}
            <div className="p-4 border-b">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold">프로젝트 선택</h3>
                  {(isContractorSelected || isCustomerSelected) && selectedUserData && (
                    <p className="text-sm text-gray-600 mt-1">
                      {selectedUserData.name} {isContractorSelected ? '소장님에게 배정된' : '고객님의'} 프로젝트만 표시됩니다.
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowProjectModal(false);
                    setProjectFilter('all');
                    setProjectSearch('');
                    setProjectPage(1);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              
              {/* 상태 탭 */}
              <div className="flex gap-2 mb-4 overflow-x-auto">
                {[
                  { value: 'all', label: '전체', color: 'bg-gray-100 text-gray-700' },
                  { value: 'received', label: '접수', color: 'bg-gray-100 text-gray-700' },
                  { value: 'review', label: '검토', color: 'bg-yellow-100 text-yellow-700' },
                  { value: 'progress', label: '진행', color: 'bg-blue-100 text-blue-700' },
                  { value: 'contract', label: '계약', color: 'bg-purple-100 text-purple-700' },
                  { value: 'completed', label: '완료', color: 'bg-green-100 text-green-700' }
                ].map((status) => (
                  <button
                    key={status.value}
                    onClick={() => {
                      setProjectFilter(status.value);
                      setProjectPage(1);
                    }}
                    className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap ${
                      projectFilter === status.value
                        ? status.color
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {status.label}
                    {projects?.total && projectFilter === status.value && (
                      <span className="ml-1">({projects.total})</span>
                    )}
                  </button>
                ))}
              </div>
              
              {/* 검색창 */}
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => {
                    setProjectSearch(e.target.value);
                    setProjectPage(1);
                  }}
                  placeholder="전화번호, 고객명, 프로젝트명으로 검색"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            
            {/* 프로젝트 목록 */}
            <div className="flex-1 overflow-y-auto p-4">
              {(isContractorSelected || isCustomerSelected) && projects?.projects?.length === 0 && !projectSearch ? (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    <p className="font-medium mb-2">
                      {isContractorSelected ? '아직 배정된 프로젝트가 없습니다.' : '진행 중인 프로젝트가 없습니다.'}
                    </p>
                    <p className="text-sm">
                      {selectedUserData?.name} {isContractorSelected ? '소장님에게 프로젝트를 배정하면' : '고객님의 프로젝트가 등록되면'} 여기에 표시됩니다.
                    </p>
                  </div>
                </div>
              ) : projects?.projects?.length > 0 ? (
                <div className="space-y-2">
                  {projects.projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => {
                        setSelectedProject(project.id.toString());
                        setShowProjectModal(false);
                        // URL 자동 생성
                        const user = users?.find(u => u.id === parseInt(selectedUser));
                        if (user && (user.user_type === 'customer' || user.user_type === 'vip_customer')) {
                          setUrl(`/h/${project.id}/${project.customer_password}`);
                        } else {
                          setUrl(`/p/${project.id}`);
                        }
                      }}
                      className="w-full p-3 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-indigo-300 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-1 text-xs rounded ${
                              project.status === 'received' ? 'bg-gray-100 text-gray-700' :
                              project.status === 'review' ? 'bg-yellow-100 text-yellow-700' :
                              project.status === 'progress' ? 'bg-blue-100 text-blue-700' :
                              project.status === 'contract' ? 'bg-purple-100 text-purple-700' :
                              project.status === 'completed' ? 'bg-green-100 text-green-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {project.status === 'received' ? '접수' :
                               project.status === 'review' ? '검토' :
                               project.status === 'progress' ? '진행' :
                               project.status === 'contract' ? '계약' :
                               project.status === 'completed' ? '완료' : '취소'}
                            </span>
                            {project.is_urgent && (
                              <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">
                                긴급
                              </span>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900">{project.title}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                            <span>👤 {project.customer?.name}</span>
                            <span>📞 {project.customer?.phone}</span>
                            {project.contractor?.name && (
                              <span>🔨 {project.contractor.name}</span>
                            )}
                          </div>
                          {project.address && (
                            <p className="text-sm text-gray-500 mt-1">📍 {project.address}</p>
                          )}
                        </div>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  {projectSearch ? '검색 결과가 없습니다.' : '프로젝트가 없습니다.'}
                </div>
              )}
            </div>
            
            {/* 페이지네이션 */}
            {projects?.total > projectsPerPage && (
              <div className="p-4 border-t flex items-center justify-between">
                <button
                  onClick={() => setProjectPage(Math.max(1, projectPage - 1))}
                  disabled={projectPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  이전
                </button>
                <span className="text-sm text-gray-700">
                  {projectPage} / {Math.ceil(projects.total / projectsPerPage)} 페이지
                </span>
                <button
                  onClick={() => setProjectPage(Math.min(Math.ceil(projects.total / projectsPerPage), projectPage + 1))}
                  disabled={projectPage >= Math.ceil(projects.total / projectsPerPage)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  다음
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* 데이터 삭제 모달 */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              <h3 className="text-lg font-bold">오래된 데이터 삭제</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  삭제할 데이터 유형
                </label>
                <select
                  value={deleteType}
                  onChange={(e) => setDeleteType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="fcm_history">FCM 발송 기록</option>
                  <option value="fcm_devices">FCM 기기 데이터</option>
                  <option value="all_fcm">모든 FCM 데이터</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  보관 기간
                </label>
                <select
                  value={deleteDays}
                  onChange={(e) => setDeleteDays(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value={1}>1일 이전 데이터 삭제</option>
                  <option value={3}>3일 이전 데이터 삭제</option>
                  <option value={7}>7일 이전 데이터 삭제</option>
                  <option value={15}>15일 이전 데이터 삭제</option>
                  <option value={30}>30일 이전 데이터 삭제</option>
                  <option value={60}>60일 이전 데이터 삭제</option>
                  <option value={90}>90일 이전 데이터 삭제</option>
                  <option value={180}>180일 이전 데이터 삭제</option>
                  <option value={365}>365일 이전 데이터 삭제</option>
                </select>
              </div>
              <p className="text-sm text-gray-600">
                선택한 기간보다 오래된 {deleteType === 'fcm_history' ? 'FCM 발송 기록' : deleteType === 'fcm_devices' ? 'FCM 기기 데이터' : '모든 FCM 데이터'}가 
                영구적으로 삭제됩니다. 이 작업은 되돌릴 수 없습니다.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                취소
              </button>
              <button
                onClick={handleDeleteOldData}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FCMTest;